/**
 * Route constants for Wonder Herbals web and mobile apps.
 * Centralized route definitions to ensure consistency across platforms.
 */

// ============================================================
// Web Routes (React Router)
// ============================================================

export const WEB_ROUTES = {
  // Public pages
  HOME: '/',
  SHOP: '/shop',
  ABOUT: '/about',
  CONTACT: '/contact',
  BLOG: '/blog',
  BLOG_POST: '/blog/:slug',
  AYURVEDA: '/ayurveda',
  DOSHA_QUIZ: '/ayurveda/dosha-quiz',
  INGREDIENTS: '/ingredients',
  INGREDIENT: '/ingredient/:slug',
  CONCERNS: '/concerns',
  CONCERN: '/concern/:slug',

  // Product routes
  PRODUCT: '/product/:slug',
  COLLECTION: '/collection/:slug',
  CATEGORY: '/category/:slug',

  // Auth routes
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  PHONE_VERIFY: '/verify-phone',

  // User account routes
  ACCOUNT: '/account',
  ACCOUNT_ORDERS: '/account/orders',
  ACCOUNT_ORDER: '/account/orders/:id',
  ACCOUNT_ADDRESSES: '/account/addresses',
  ACCOUNT_PROFILE: '/account/profile',
  ACCOUNT_WISHLIST: '/account/wishlist',
  ACCOUNT_LOYALTY: '/account/loyalty',
  ACCOUNT_DOSHA: '/account/dosha',

  // Checkout routes
  CART: '/cart',
  CHECKOUT: '/checkout',
  CHECKOUT_SHIPPING: '/checkout/shipping',
  CHECKOUT_PAYMENT: '/checkout/payment',
  CHECKOUT_REVIEW: '/checkout/review',
  ORDER_CONFIRMATION: '/order/:orderNumber/confirmation',
  PAYMENT_SUCCESS: '/payment/success',
  PAYMENT_FAILED: '/payment/failed',

  // Legal pages
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_OF_SERVICE: '/terms-of-service',
  REFUND_POLICY: '/refund-policy',
  SHIPPING_POLICY: '/shipping-policy',

  // Search
  SEARCH: '/search',

  // Tracking
  TRACK: '/track/:id',
} as const;

// ============================================================
// Mobile Routes (Expo Router)
// ============================================================

export const MOBILE_ROUTES = {
  // Tab screens
  HOME: '/',
  SHOP: '/shop',
  AYURVEDA: '/ayurveda',
  CART: '/cart',
  PROFILE: '/profile',

  // Detail screens
  PRODUCT: '/product/:slug',
  INGREDIENT: '/ingredient/:slug',
  CONCERN: '/concern/:slug',
  COLLECTION: '/collection/:slug',
  CATEGORY: '/category/:slug',
  BLOG_POST: '/blog/:slug',

  // Auth screens
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',

  // Account screens
  ACCOUNT_ORDERS: '/account/orders',
  ACCOUNT_ORDER: '/account/orders/:id',
  ACCOUNT_ADDRESSES: '/account/addresses',
  ACCOUNT_PROFILE: '/account/profile',
  ACCOUNT_DOSHA: '/account/dosha',

  // Checkout
  CHECKOUT: '/checkout',
  ORDER_CONFIRMATION: '/order/:orderNumber/confirmation',

  // Search
  SEARCH: '/search',

  // Dosha Quiz
  DOSHA_QUIZ: '/ayurveda/dosha-quiz',
} as const;

// ============================================================
// Admin Routes
// ============================================================

export const ADMIN_ROUTES = {
  // Dashboard
  DASHBOARD: '/',
  LOGIN: '/login',

  // Content management
  PRODUCTS: '/products',
  PRODUCT_NEW: '/products/new',
  PRODUCT_EDIT: '/products/:id',
  INGREDIENTS: '/ingredients',
  INGREDIENT_NEW: '/ingredients/new',
  INGREDIENT_EDIT: '/ingredients/:id',
  CONCERNS: '/concerns',
  CONCERN_NEW: '/concerns/new',
  CONCERN_EDIT: '/concerns/:id',
  COLLECTIONS: '/collections',
  COLLECTION_NEW: '/collections/new',
  COLLECTION_EDIT: '/collections/:id',
  BLOGS: '/blogs',
  BLOG_NEW: '/blogs/new',
  BLOG_EDIT: '/blogs/:id',
  PAGES: '/pages',
  PAGE_EDIT: '/pages/:id',

  // Homepage builder
  HOMEPAGE: '/homepage',

  // Order management
  ORDERS: '/orders',
  ORDER_DETAIL: '/orders/:id',

  // Customer management
  CUSTOMERS: '/customers',
  CUSTOMER_DETAIL: '/customers/:id',

  // Reviews
  REVIEWS: '/reviews',

  // Marketing
  COUPONS: '/coupons',
  COUPON_NEW: '/coupons/new',
  FOLLOWUPS: '/followups',

  // Chat inbox
  CHAT: '/chat',

  // Analytics
  ANALYTICS: '/analytics',

  // Settings
  SETTINGS: '/settings',

  // Tasks
  TASKS: '/tasks',

  // Meta Hub
  META_HUB: '/meta-hub',

  // Instagram
  INSTAGRAM: '/instagram',

  // WhatsApp
  WHATSAPP_HUB: '/whatsapp',
  WHATSAPP_BRAIN: '/whatsapp/brain',

  // SEO
  SEO_DASHBOARD: '/seo',

  // Logistics
  LOGISTICS: '/logistics',

  // Staff
  STAFF: '/staff',

  // Mobile App
  MOBILE_APP: '/mobile',

  // Import
  IMPORT_SHOPIFY: '/import/shopify',
} as const;

// ============================================================
// API Routes
// ============================================================

export const API_ROUTES = {
  // Products
  PRODUCTS: '/api/products',
  PRODUCT: '/api/products/:id',
  PRODUCT_BY_SLUG: '/api/products/slug/:slug',

  // Ingredients
  INGREDIENTS: '/api/ingredients',
  INGREDIENT: '/api/ingredients/:id',
  INGREDIENT_BY_SLUG: '/api/ingredients/slug/:slug',

  // Concerns
  CONCERNS: '/api/concerns',
  CONCERN: '/api/concerns/:id',
  CONCERN_BY_SLUG: '/api/concerns/slug/:slug',

  // Collections
  COLLECTIONS: '/api/collections',
  COLLECTION: '/api/collections/:id',
  COLLECTION_BY_SLUG: '/api/collections/slug/:slug',

  // Orders
  ORDERS: '/api/orders',
  ORDER: '/api/orders/:id',
  ORDER_BY_NUMBER: '/api/orders/number/:orderNumber',

  // Payments
  RAZORPAY_CREATE: '/api/payments/razorpay/create',
  RAZORPAY_VERIFY: '/api/payments/razorpay/verify',
  STRIPE_CREATE: '/api/payments/stripe/create',
  STRIPE_VERIFY: '/api/payments/stripe/verify',

  // Shipping
  SHIPPING_RATES: '/api/shipping/rates',
  SHIPPING_TRACK: '/api/shipping/track/:orderId',

  // Auth
  AUTH_VERIFY: '/api/auth/verify',
  AUTH_PROFILE: '/api/auth/profile',

  // Search
  SEARCH: '/api/search',

  // Upload
  UPLOAD_PRESIGN: '/api/upload/presign',

  // Webhooks
  WEBHOOK_RAZORPAY: '/api/webhooks/razorpay',
  WEBHOOK_STRIPE: '/api/webhooks/stripe',
  WEBHOOK_SHIPROCKET: '/api/webhooks/shiprocket',

  // Communication
  WHATSAPP_SEND: '/api/whatsapp/send',
  EMAIL_SEND: '/api/email/send',

  // Reviews
  REVIEWS: '/api/reviews',
  PRODUCT_REVIEWS: '/api/reviews/product/:productId',

  // Blog
  BLOGS: '/api/blogs',
  BLOG: '/api/blogs/:id',
  BLOG_BY_SLUG: '/api/blogs/slug/:slug',

  // Admin
  ADMIN_DASHBOARD: '/api/admin/dashboard',
  ADMIN_STATS: '/api/admin/stats',
  ADMIN_BULK: '/api/admin/bulk',

  // Coupons
  COUPONS: '/api/coupons',
  COUPON_VALIDATE: '/api/coupons/validate',
} as const;

// ============================================================
// Helper: Generate web product URL
// ============================================================

export function getProductUrl(slug: string): string {
  return `/product/${slug}`;
}

export function getIngredientUrl(slug: string): string {
  return `/ingredient/${slug}`;
}

export function getConcernUrl(slug: string): string {
  return `/concern/${slug}`;
}

export function getCollectionUrl(slug: string): string {
  return `/collection/${slug}`;
}

export function getCategoryUrl(slug: string): string {
  return `/category/${slug}`;
}

export function getBlogUrl(slug: string): string {
  return `/blog/${slug}`;
}
